import React from "react";
import { useState } from "react";
import { useSelector, useDispatch } from "types/store";
import Select from "react-select";
import { Button, ButtonGroup } from "aible";
import Tooltip from "components/Inputs/Tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronUp, faChevronDown } from "@fortawesome/free-solid-svg-icons";
import * as homeReducer from "containers/Home/reducer";
import * as chatGPTReducer from "containers/ChatGPT/reducer";
import styles from "./style.module.css";
const KPIAdvancedSettings = ({ datasetId }) => {
    // Advanced Settings Controls are settings saved to dataset options
    // It is split out and uses props in order to reuse the component in the data card
    const dispatch = useDispatch();
    const dataset = useSelector((state) => homeReducer.getDataset(state, datasetId));
    const KPIColumns = useSelector((state) => homeReducer.getKPIColumns(state, datasetId));
    const KPIAnalysisCalcModes = useSelector((state) => chatGPTReducer.getKPIAnalysisColumnCalcModes(state));
    const updateKPIAnalysisColumnCalcModeByKPI = (key, data) => dispatch(chatGPTReducer.updateKPIAnalysisColumnCalcModeByKPI(key, data));
    const KPITopChartsOrder = useSelector((state) => chatGPTReducer.getKPITopChartsOrderRaw(state)) ?? [];
    const updateKPITopChartsOrder = (data) => dispatch(chatGPTReducer.updateKPITopChartsOrder(data));
    return (React.createElement("div", { className: styles.settingsWrapper },
        React.createElement(AdvancedSettingsControls, { KPIAnalysisCalcModes: KPIAnalysisCalcModes, updateKPIAnalysisColumnCalcModeByKPI: updateKPIAnalysisColumnCalcModeByKPI, KPITopChartsOrder: KPITopChartsOrder, updateKPITopChartsOrder: updateKPITopChartsOrder, datasetId: datasetId })));
};
export const SettingsKPISelection = ({ value, options, onChange, placeholder, menuPlacement }) => {
    const addColOptions = options.map((c) => ({ label: c, value: c }));
    const selectedValues = addColOptions.filter((option) => value?.includes(option.value));
    return (React.createElement(Select, { value: selectedValues, options: addColOptions, onChange: (val) => {
            console.log(val);
            if (!val) {
                onChange([]);
            }
            const retVal = val ?? [];
            const retAdd = retVal.map((o) => o.value);
            onChange(retAdd);
        }, isSearchable: true, placeholder: placeholder, isMulti: true, isClearable: true, maxMenuHeight: 200, menuPlacement: menuPlacement ?? "top" }));
};
// helper swap function, does not mutate
const swapArrayElements = (array, fromIndex, toIndex) => {
    if (fromIndex === toIndex || fromIndex >= array.length || fromIndex < 0 || toIndex >= array.length || toIndex < 0) {
        return array;
    }
    const retArray = [...array];
    const mElem = retArray[fromIndex];
    retArray[fromIndex] = retArray[toIndex];
    retArray[toIndex] = mElem;
    return retArray;
};
// Splitting this out in order to allow Dataset advanced settings to use this
export const AdvancedSettingsControls = ({ KPIAnalysisCalcModes, updateKPIAnalysisColumnCalcModeByKPI, KPITopChartsOrder, updateKPITopChartsOrder, datasetId }) => {
    const dispatch = useDispatch();
    const [modifyCalcColumns, updateModifyCalcColumns] = useState([]);
    const selectedKpiColumns = useSelector((state) => homeReducer.getKPIColumns(state, datasetId));
    const allKpiColumns = useSelector((state) => homeReducer.getKPIColumns(state, datasetId, true));
    const pinnedColumns = useSelector((state) => homeReducer.getKPIAnalysisPinnedCharts(state, datasetId));
    const updateKpiPinnedColumns = (val) => dispatch(homeReducer.updateKPIAnalysisPinnedCharts(datasetId, val));
    const excludeColumns = useSelector((state) => homeReducer.getKPIAnalysisExcludeColumns(state, datasetId));
    const unpinnedColumns = allKpiColumns.filter((c) => !pinnedColumns?.includes(c));
    const defaultCalcColumns = allKpiColumns?.filter((c) => !KPIAnalysisCalcModes?.[c]) ?? [];
    const modifiedCalcColumns = allKpiColumns?.filter((c) => KPIAnalysisCalcModes?.[c]) ?? [];
    const allItemsSelected = allKpiColumns?.length === selectedKpiColumns?.length;
    const dataset = useSelector((state) => homeReducer.getDataset(state, datasetId));
    const isDatasetLoading = dataset?.status_name !== "DONE" && dataset?.status_name !== "ERROR";
    const handleToggleAllItems = () => {
        if (isDatasetLoading) {
            return;
        }
        if (allItemsSelected) {
            const retExcludeColumns = [...allKpiColumns];
            if (KPITopChartsOrder?.length > 0) {
                updateKPITopChartsOrder([]);
            }
            return dispatch(homeReducer.updateKPIAnalysisExcludeColumns(datasetId, retExcludeColumns));
        }
        else {
            return dispatch(homeReducer.updateKPIAnalysisExcludeColumns(datasetId, []));
        }
    };
    const sortedKpi = [...(pinnedColumns ?? []), ...unpinnedColumns];
    return (React.createElement("div", { className: styles.settingsWrapper },
        React.createElement("div", { className: styles.settingsItem },
            React.createElement("div", { className: styles.settingsItemTitle }, `${selectedKpiColumns?.length} KPIs will be analyzed.`),
            React.createElement("div", { className: styles.settingsLabel }, "You can also select specific ones to pin (highlight) in the report and change the calculation method"),
            React.createElement("div", { className: styles.advancedSettingsKpiTableContainer },
                React.createElement("div", { className: styles.advancedSettingsKpiTableItem },
                    React.createElement("div", { style: { display: "flex", flex: "1" }, onClick: handleToggleAllItems },
                        React.createElement("input", { className: styles.kpiTableCheckbox, type: "checkbox", readOnly: true, checked: allItemsSelected, disabled: isDatasetLoading }),
                        React.createElement("div", { className: styles.advancedSettingsKpiTableLabel, style: { flex: "1", textAlign: "left" } }, "KPI Name")),
                    React.createElement("div", { className: styles.advancedSettingsKpiTableLabel, style: { flexBasis: "148px" } }, "Pin Chart"),
                    React.createElement("div", { className: styles.advancedSettingsKpiTableLabel }, "Calculation Method")),
                sortedKpi.map((kpi, index) => {
                    return (React.createElement(KpiTableItem, { kpi: kpi, datasetId: datasetId, index: index, KPITopChartsOrder: KPITopChartsOrder, updateKPITopChartsOrder: updateKPITopChartsOrder, updateKPIAnalysisColumnCalcModeByKPI: updateKPIAnalysisColumnCalcModeByKPI }));
                })))));
};
// Leaving this here due to merge conflict
export const KPIDeepDiveExcludeColumnsSetting = ({ KPIDeepDiveExcludeColumns, updateKPIDeepDiveExcludeColumns, KPIDeepDiveExcludeColumnOptions, menuPlacement }) => {
    const retColumns = KPIDeepDiveExcludeColumnOptions.filter((o) => !KPIDeepDiveExcludeColumns?.includes(o));
    return (React.createElement("div", { className: styles.settingsItem },
        React.createElement("div", { className: styles.settingsItemTitle },
            "Select KPIs for an additional What's Changed Deep Dive Analysis",
            React.createElement(Tooltip, { overlay: "The What's Changed Deep Dive Analysis looks into what caused a selected KPI to change. It will analyze these columns and how they interact with that KPI. If the selected KPI is excluded, it will be still be analyzed, but it will not be considered when analyzing other KPIs." })),
        React.createElement("div", { className: styles.settingsLabel }, "This can also be done after the KPI report is generated"),
        React.createElement(SettingsKPISelection, { value: retColumns, options: KPIDeepDiveExcludeColumnOptions, onChange: (v) => {
                const exclude = KPIDeepDiveExcludeColumnOptions.filter((o) => !v.includes(o));
                updateKPIDeepDiveExcludeColumns(exclude);
            }, placeholder: "Select KPIs", menuPlacement: menuPlacement })));
};
const KpiTableItem = ({ kpi, datasetId, index, updateKPIAnalysisColumnCalcModeByKPI, KPITopChartsOrder, updateKPITopChartsOrder }) => {
    const dispatch = useDispatch();
    const selectedKpiColumns = useSelector((state) => homeReducer.getKPIColumns(state, datasetId));
    const excludeColumns = useSelector((state) => homeReducer.getKPIAnalysisExcludeColumns(state, datasetId));
    const kpiAnalysisColumnCalcModeForKpi = useSelector((state) => homeReducer.getKPIAnalysisColumnCalcModeForKPI(state, datasetId, kpi));
    const isKpiSelected = selectedKpiColumns?.includes(kpi);
    const isKpiPinned = KPITopChartsOrder?.includes(kpi);
    const dataset = useSelector((state) => homeReducer.getDataset(state, datasetId));
    const isDatasetLoading = dataset?.status_name !== "DONE" && dataset?.status_name !== "ERROR";
    const handleToggleSelectedKpi = () => {
        if (isDatasetLoading) {
            return;
        }
        if (isKpiSelected) {
            const retExcludeColumns = [...(excludeColumns ?? []), kpi];
            if (isKpiPinned) {
                const retPinned = KPITopChartsOrder.filter((col) => col !== kpi);
                updateKPITopChartsOrder(retPinned);
            }
            dispatch(homeReducer.updateKPIAnalysisExcludeColumns(datasetId, retExcludeColumns));
        }
        else {
            const retExcludeColumns = excludeColumns?.filter((col) => col !== kpi);
            dispatch(homeReducer.updateKPIAnalysisExcludeColumns(datasetId, retExcludeColumns));
        }
    };
    const handleTogglePinned = () => {
        if (isDatasetLoading) {
            return;
        }
        if (isKpiPinned) {
            const retPinned = KPITopChartsOrder?.filter((col) => col !== kpi);
            updateKPITopChartsOrder(retPinned);
        }
        else {
            const retPinned = [...(KPITopChartsOrder ?? []), kpi];
            updateKPITopChartsOrder(retPinned);
        }
    };
    return (React.createElement("div", { className: styles.advancedSettingsKpiTableItem },
        React.createElement("div", { style: { display: "flex", flex: "1" }, onClick: handleToggleSelectedKpi },
            React.createElement("input", { className: styles.kpiTableCheckbox, type: "checkbox", checked: isKpiSelected, disabled: isDatasetLoading }),
            React.createElement("div", { className: styles.kpiTableItem, style: { flex: "1", textAlign: "left", justifyContent: "start" } }, kpi)),
        React.createElement("div", { className: styles.kpiTableItem, style: { flexBasis: "148px" } },
            React.createElement("div", { className: styles.kpiTablePinControls, onClick: (e) => e.stopPropagation() },
                React.createElement("input", { className: styles.kpiTableCheckbox, style: { margin: "-2px 0px 0px !important" }, type: "checkbox", checked: isKpiPinned, onClick: (e) => {
                        e.stopPropagation();
                        handleTogglePinned();
                    }, disabled: !isKpiSelected || isDatasetLoading }),
                isKpiPinned && (React.createElement(ButtonGroup, null,
                    React.createElement(Button, { style: {
                            height: "30px"
                        }, disabled: index <= 0 || isDatasetLoading, onClick: (e) => {
                            e.stopPropagation();
                            const retPinned = swapArrayElements(KPITopChartsOrder, index, index - 1);
                            updateKPITopChartsOrder(retPinned);
                        }, isSmall: true },
                        React.createElement(FontAwesomeIcon, { icon: faChevronUp })),
                    React.createElement(Button, { style: {
                            height: "30px"
                        }, disabled: index >= KPITopChartsOrder?.length - 1 || isDatasetLoading, onClick: (e) => {
                            e.stopPropagation();
                            const retPinned = swapArrayElements(KPITopChartsOrder, index, index + 1);
                            updateKPITopChartsOrder(retPinned);
                        }, isSmall: true },
                        React.createElement(FontAwesomeIcon, { icon: faChevronDown })))))),
        React.createElement("div", { className: styles.kpiTableItem },
            React.createElement(ButtonGroup, null,
                React.createElement(Button, { isSmall: true, disabled: !isKpiSelected || isDatasetLoading, onClick: () => {
                        updateKPIAnalysisColumnCalcModeByKPI(kpi, "SUM");
                    }, active: kpiAnalysisColumnCalcModeForKpi === "SUM" }, "Total"),
                React.createElement(Button, { isSmall: true, disabled: !isKpiSelected || isDatasetLoading, onClick: () => {
                        updateKPIAnalysisColumnCalcModeByKPI(kpi, "AVERAGE");
                    }, active: kpiAnalysisColumnCalcModeForKpi === "AVERAGE" }, "Average")))));
};
export default KPIAdvancedSettings;
